import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Stack,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  InfoRow,
} from '../components';
import {
  useTransfer,
  useShipTransfer,
  useReceiveTransfer,
} from '../hooks/useStockTransfers';
import { colors } from '../constants';

export default function StockTransferDetail() {
  const { uuid } = useParams();
  const { data: transfer, isLoading } = useTransfer(uuid);
  const ship = useShipTransfer();
  const receive = useReceiveTransfer();
  const [toast, setToast] = useState(null);

  if (isLoading) return <LoadingState />;
  if (!transfer) {
    return (
      <>
        <PageHeader title="Not Found" backTo="/inventory-management/stock-transfers" />
        <Typography sx={{ color: colors.gray500 }}>Item not found</Typography>
      </>
    );
  }

  const handleShip = async () => {
    try {
      await ship.mutateAsync(transfer.uuid);
      setToast({ severity: 'success', message: 'Transfer shipped' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Ship failed' });
    }
  };

  const handleReceive = async () => {
    try {
      await receive.mutateAsync({ uuid: transfer.uuid, data: { receivedBy: 'partner' } });
      setToast({ severity: 'success', message: 'Transfer received' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Receive failed' });
    }
  };

  return (
    <>
      <PageHeader
        title={`Transfer ${transfer.uuid.slice(0, 8)}`}
        backTo="/inventory-management/stock-transfers"
        actions={<StatusChip status={transfer.status} />}
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard title="Details">
            <InfoRow label="From" value={transfer.fromWarehouseName || transfer.fromWarehouseUuid} />
            <InfoRow label="To" value={transfer.toWarehouseName || transfer.toWarehouseUuid} />
            <InfoRow label="Notes" value={transfer.notes} />
          </DetailCard>

          <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
            {transfer.status === 'draft' && (
              <Button
                variant="contained"
                startIcon={<SendOutlinedIcon />}
                onClick={handleShip}
                disabled={ship.isPending}
                sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
              >
                Ship
              </Button>
            )}
            {transfer.status === 'in_transit' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DoneAllIcon />}
                onClick={handleReceive}
                disabled={receive.isPending}
                sx={{ textTransform: 'none' }}
              >
                Receive
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard title="Items">
            {transfer.items?.map((item, idx) => (
              <Stack
                key={idx}
                direction="row"
                justifyContent="space-between"
                sx={{ py: 1, borderBottom: `1px solid ${colors.gray100}` }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.sku}</Typography>
                  <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.productType}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.quantity}</Typography>
                  {item.receivedQuantity != null && (
                    <Typography variant="caption" sx={{ color: colors.gray400 }}>
                      Received: {item.receivedQuantity}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </DetailCard>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
